import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Icon1 from "@assets/icons/Icon";
import SearchField from "@components/ui/textfields/SearchField";
import { useAtelierStore } from "@store/atelierStore";
import { useDataStore } from "@store/user_org";
import { useDispoAtelierStore } from "@store/dispoAtelierStore";
import { useAuthStore } from "@store/auth";
import { ordresApi } from "@services/api/ordres";
import type { Ordre, OrdreStatus } from "types/ordre";

import VehicleImage from "@components/ui/VehicleImage";
import { MobileTabBar } from "./MobileTabBar";

const STATUS_DISPLAY: Partial<Record<OrdreStatus, { label: string; bg: string; color: string }>> = {
  rdv: { label: "rdv", bg: "#F3F4F6", color: "#9CA3AF" },
  a_preparer: { label: "en prépa", bg: "#F4F4F4", color: "#A1A1A1" },
  devis_initial: { label: "devis initial", bg: "#FEF3C7", color: "#D97706" },
  commande: { label: "commandé", bg: "#ECFFFE", color: "#47DDD5" },
  pret: { label: "prêt", bg: "#E8EAFF", color: "#5C6BFF" },
  reception: { label: "réceptionné", bg: "#F4F4F4", color: "#A1A1A1" },
  en_cours: { label: "en cours", bg: "#CACFFF", color: "#5C6BFF" },
  devis_supp: { label: "devis supp.", bg: "#FEF3C7", color: "#D97706" },
  accord: { label: "accord", bg: "#D1FAE5", color: "#059669" },
  acompte: { label: "acompte", bg: "#FEF3C7", color: "#D97706" },
  fin_de_travaux: { label: "terminé", bg: "#D1FAE5", color: "#059669" },
  facturation: { label: "facturation", bg: "#E8EAFF", color: "#6366f1" },
  solde: { label: "solde", bg: "#FEF3C7", color: "#D97706" },
  solde_paye: { label: "solde payé", bg: "#D1FAE5", color: "#059669" },
  disponible: { label: "disponible", bg: "#F1E7F9", color: "#8258FF" },
  restitue: { label: "restitué", bg: "#D1FAE5", color: "#059669" },
  arrete: { label: "arrêté", bg: "#FEE2E2", color: "#B91C1C" },
};

// ─── Component ──────────────────────────────────────────────

export default function MobileOrdresScreen() {
  const navigate = useNavigate();
  const orgData = useDataStore((s) => s.orgData);
  const userData = useDataStore((s) => s.userData);
  const logout = useAuthStore((s) => s.logout);
  const fetchData = useAtelierStore((s) => s.fetchData);
  const fetchBlocks = useDispoAtelierStore((s) => s.fetchBlocks);
  const jobs = useAtelierStore((s) => s.jobs);

  const [search, setSearch] = useState("");
  const [ordres, setOrdres] = useState<Ordre[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch atelier data to get jobs assigned to current user
  useEffect(() => {
    if (orgData?.id) {
      fetchData(orgData.id);
      fetchBlocks(orgData.id);
    }
  }, [orgData?.id]);

  // Get ordre IDs assigned to this mechanic from atelier jobs
  const myOrdreIds = jobs
    .filter((j) => j.workerId === userData?.id)
    .map((j) => j.ordre_id)
    .filter((id): id is string => !!id);
  const uniqueOrdreIds = Array.from(new Set(myOrdreIds));

  // Fetch ordres in a single batch request
  const loadOrdres = useCallback(async () => {
    if (!uniqueOrdreIds.length) {
      setOrdres([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const results = await ordresApi.batch(uniqueOrdreIds);
      setOrdres(results);
    } catch {
      setOrdres([]);
    }
    setLoading(false);
  }, [uniqueOrdreIds.join(",")]);

  useEffect(() => {
    loadOrdres();
  }, [loadOrdres]);

  const filtered = search.trim()
    ? ordres.filter((o) => {
        const q = search.toLowerCase();
        return o.name.toLowerCase().includes(q) || o.num.toLowerCase().includes(q) || (o.immatriculation || "").toLowerCase().includes(q);
      })
    : ordres;

  return (
    <div className="flex flex-col bg-white" style={{ fontFamily: "Poppins", height: "100dvh" }}>
      {/* Header */}
      <div className="flex flex-row items-center justify-between" style={{ padding: "max(30px, env(safe-area-inset-top, 30px)) 20px 0" }}>
        <span style={{ fontWeight: 500, fontSize: 20, lineHeight: "30px", color: "#000000" }}>
          Ordres en cours
        </span>
        <button onClick={logout} className="flex items-center justify-center" style={{ padding: "0 10px" }}>
          <Icon1 name="logout" size={20} color="#A1A1A1" />
        </button>
      </div>

      {/* Tab bar */}
      <div style={{ padding: "16px 20px 0" }}>
        <MobileTabBar active="ordres" />
      </div>

      {/* Search */}
      <div style={{ padding: "16px 20px 0" }}>
        <SearchField value={search} onChange={setSearch} placeholder="Rechercher .." />
      </div>

      {/* Section label */}
      <div style={{ padding: "16px 20px 5px" }}>
        <span style={{ fontWeight: 500, fontSize: 13, lineHeight: "20px", color: "#A8A8A8" }}>
          Vos ordres assignés
        </span>
      </div>

      {/* Orders list */}
      <div className="flex-1 overflow-y-auto" style={{ padding: "0 20px 20px" }}>
        {loading ? (
          <div className="flex flex-col" style={{ gap: 7 }}>
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="flex flex-row items-stretch animate-pulse"
                style={{ padding: 4, gap: 10, background: "#FFFFFF", boxShadow: "0px 2px 6.3px rgba(0,0,0,0.10)", borderRadius: 10 }}
              >
                <div style={{ width: 69, height: 64, background: "#F2F2F2", borderRadius: 10, flexShrink: 0 }} />
                <div className="flex flex-col justify-center flex-1" style={{ gap: 6 }}>
                  <div style={{ height: 12, background: "#F2F2F2", borderRadius: 4, width: "60%" }} />
                  <div style={{ height: 10, background: "#F2F2F2", borderRadius: 4, width: "40%" }} />
                  <div style={{ height: 10, background: "#F2F2F2", borderRadius: 4, width: "30%" }} />
                </div>
                <div className="flex items-end flex-shrink-0" style={{ paddingBottom: 4 }}>
                  <div style={{ height: 24, width: 60, background: "#F2F2F2", borderRadius: 5 }} />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16" style={{ gap: 12 }}>
            <div
              className="flex items-center justify-center"
              style={{ width: 56, height: 56, borderRadius: 16, background: "#F4F4F4" }}
            >
              <Icon1 name="car" size={28} color="#C4C4C4" />
            </div>
            <div className="flex flex-col items-center" style={{ gap: 4 }}>
              <span style={{ fontWeight: 600, fontSize: 14, color: "#1E1E1E" }}>
                {search ? "Aucun résultat" : "Aucun ordre assigné"}
              </span>
              <span style={{ fontWeight: 400, fontSize: 12, color: "#A8A8A8", textAlign: "center", maxWidth: 220 }}>
                {search
                  ? "Essayez un autre terme de recherche"
                  : "Les ordres qui vous sont assignés apparaîtront ici"}
              </span>
            </div>
          </div>
        ) : (
          <div className="flex flex-col" style={{ gap: 7 }}>
            {filtered.map((ordre) => {
              const status = STATUS_DISPLAY[ordre.status];
              const vehicle = ordre.vehicle;
              // Find next/earliest scheduled atelier job for this ordre
              const ordreJobs = jobs
                .filter((j) => j.ordre_id === ordre.id && j.workerId === userData?.id)
                .sort((a, b) => a.date.localeCompare(b.date) || a.startHour - b.startHour);
              const nextJob = ordreJobs[0];
              const scheduledLabel = nextJob
                ? (() => {
                    const d = new Date(nextJob.date);
                    const day = d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
                    const h = Math.floor(nextJob.startHour);
                    const m = nextJob.startHour % 1 === 0.5 ? "30" : "00";
                    return `${day} · ${h}h${m}`;
                  })()
                : null;
              return (
                <button
                  key={ordre.id}
                  onClick={() => navigate(`/m/ordre/${ordre.id}`)}
                  className="flex flex-row items-stretch text-left"
                  style={{
                    padding: 4, gap: 10,
                    background: "#FFFFFF",
                    boxShadow: "0px 2px 6.3px rgba(0, 0, 0, 0.15)",
                    borderRadius: 10,
                  }}
                >
                  {/* Vehicle thumbnail */}
                  <div
                    className="flex items-center justify-center flex-shrink-0"
                    style={{ width: 69, height: 64, background: "#F2F2F2", borderRadius: 10, overflow: "hidden" }}
                  >
                    {vehicle ? (
                      <VehicleImage marque={vehicle.marque} modele={vehicle.modele} coloris={vehicle.coloris} mec={vehicle.mec} className="w-[59px]" iconSize={24} />
                    ) : (
                      <Icon1 name="car" size={24} color="#A1A1A1" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex flex-col justify-center flex-1" style={{ padding: "4px 0", gap: 3, minWidth: 0 }}>
                    <span className="truncate" style={{ fontWeight: 500, fontSize: 12, lineHeight: "18px", color: "#1E1E1E" }}>
                      {ordre.name}
                    </span>
                    <div className="flex flex-col" style={{ gap: 2 }}>
                      <span className="truncate" style={{ fontWeight: 500, fontSize: 10, lineHeight: "15px", color: "#A8A8A8" }}>
                        {ordre.num}
                      </span>
                      <span className="truncate" style={{ fontWeight: 500, fontSize: 10, lineHeight: "15px", color: "#A8A8A8" }}>
                        {ordre.immatriculation || ordre.vehicle?.plate || "—"}
                      </span>
                      {scheduledLabel && (
                        <span className="truncate" style={{ fontWeight: 500, fontSize: 10, lineHeight: "15px", color: "#6366f1" }}>
                          {scheduledLabel}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Status badge */}
                  {status && (
                    <div
                      className="flex items-center justify-center flex-shrink-0 self-end"
                      style={{
                        padding: "4px 10px",
                        background: status.bg,
                        borderRadius: 5,
                      }}
                    >
                      <span style={{ fontWeight: 500, fontSize: 12, lineHeight: "18px", color: status.color }}>
                        {status.label}
                      </span>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Bottom indicator */}
      <div className="flex justify-center" style={{ padding: "10px 0 max(8px, env(safe-area-inset-bottom, 8px))" }}>
        <div style={{ width: 104, height: 5, background: "#000000", borderRadius: 30 }} />
      </div>
    </div>
  );
}
